// https://www.dbos.dev/solutions/cron-hosting

import { SiteLayout } from '@components/layout/SiteLayout';
import { Dropdown, DropdownList, DropdownToggle } from '@components/webflow';

export function SolutionsCronHostingPage() {
  return (
    <SiteLayout banner={true} footerCta={null}>
      <div className="background-glow-top-right">
        <section className="hero-center-wrapper">
          <h1 className="text-heading-caption">{'DURABLe\u00a0Cron Jobs'}</h1>
          <h2 className="text-heading-h1">
            <span className="text-gradient">Failure-Proof</span> <br />
            Cron Jobs
          </h2>
          <p className="text-body">
            Create failure-proof cron jobs in minutes.
            <br />
            Run them anywhere or on DBOS Cloud.
          </p>
          <div className="button-row-vertical">
            <div className="button-primary-border">
              <a
                href="https://console.dbos.dev/"
                className="button-cta button-primary button-navigation w-inline-block"
              >
                <div className="text-block">Start building</div>
              </a>
            </div>
            <div className="text-caption-micro">No credit card required.</div>
          </div>
          <div className="card-creators-small">
            <p className="text-body-highlight">From the creators of Postgres and Apache Spark</p>
          </div>
        </section>
        <div className="spacer-3x shortcron" />
      </div>
      <div className="background-glow-center-left">
        <section className="section-wrapper">
          <div className="card-cta-large-wrapper">
            <div className="card-cta-large-glow-right">
              <div className="cta-large-content-left">
                <h2 className="text-heading-h2">
                  {'Cron jobs that run without fail, '}
                  <br />
                  {'\u200d'}
                  <span className="text-gradient">no matter what.</span>
                </h2>
                <p className="text-body text-alignment-left">
                  Build fail-safe cron jobs with Python and crontab.
                  <br />
                  Run them anywhere or on DBOS Cloud.
                </p>
                <div className="button-row-horizontal">
                  <div className="button-primary-border">
                    <a
                      href="https://console.dbos.dev/"
                      className="button-cta button-primary button-navigation w-inline-block"
                    >
                      <div className="text-block">Try DBOS Cloud</div>
                    </a>
                  </div>
                  <div className="text-caption-micro">No credit card required.</div>
                </div>
                <h3 className="text-list-caption">create next-level cron jobs</h3>
                <ul role="list" className="text-list">
                  <li className="text-list-item">Reliable execution saves hours of manual fixes.</li>
                  <li className="text-list-item">Built-in resilience reduces code complexity.</li>
                  <li className="text-list-item">OpenTelemetry tracing to speed up troubleshooting.</li>
                </ul>
              </div>
              <div className="card-benefit-code-block">
                <div style={{ paddingTop: '56.17021276595745%' }} className="w-embed-youtubevideo youtube">
                  <iframe
                    src="https://www.youtube.com/embed/CnH8oAfCUh8?rel=0&controls=1&autoplay=0&mute=0&start=0"
                    frameBorder="0"
                    style={{
                      position: 'absolute',
                      left: '0',
                      top: '0',
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'auto',
                    }}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="Getting started with Cloud Cron on DBOS"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        <div className="spacer-3x" />
      </div>
      <section id="how-it-works" className="section-wrapper">
        <div className="card-how-it-works">
          <h2 className="text-heading-caption">How it works</h2>
          <div className="w-layout-hflex flex-block-8">
            <div>
              <h3 className="heading-style-h3">Launch durable cron jobs in 4 easy steps</h3>
              <p className="text-body text-alignment-left">We’re not kidding, DBOS is effortless.</p>
            </div>
            <div className="button-row-horizontal">
              <div className="text-caption-micro">No credit card required.</div>
              <div className="button-primary-border">
                <a
                  href="https://console.dbos.dev/"
                  className="button-cta button-primary button-navigation w-inline-block"
                >
                  <div className="text-block">Start for free</div>
                </a>
              </div>
            </div>
          </div>
          <div className="w-layout-hflex flex-block-9">
            <a href="https://console.dbos.dev/launch" className="card-how-it-works-item w-inline-block">
              <p className="text-caption-pill text-alignment-right">{'<1 min'}</p>
              <div className="div-block-30" />
              <img
                width="20"
                height="20"
                alt=""
                loading="lazy"
                src="/assets/images/6758d7c1daeb1ffb5868934c_user-plus.svg"
                className="card-how-it-works-icon"
              />
              <h4 className="text-heading-h5">1. Create a free DBOS Pro account</h4>
              <p className="text-body text-alignment-left">For building, managing, hosting your cron jobs.</p>
              <div className="card-how-it-works-overlay" />
              <img
                src="/assets/images/6758d7797dd2d71709a4b164_sign-up.png"
                loading="lazy"
                alt=""
                className="card-how-it-works-background"
              />
            </a>
            <a href="https://console.dbos.dev/launch" className="card-how-it-works-item w-inline-block">
              <p className="text-caption-pill text-alignment-right">1 min</p>
              <div className="div-block-30" />
              <img
                width="20"
                height="20"
                alt=""
                loading="lazy"
                src="/assets/images/6758d7c151a010c8985b8836_package.svg"
                className="card-how-it-works-icon"
              />
              <h4 className="text-heading-h5">2. Launch ‘Cron Starter’ template</h4>
              <p className="text-body text-alignment-left">Creates a durable cron job template for you.</p>
              <div className="card-how-it-works-overlay" />
              <img
                src="/assets/images/6758d77992fecccbbc873ece_cron-starter.png"
                loading="lazy"
                alt=""
                className="card-how-it-works-background"
              />
            </a>
            <a href="https://console.dbos.dev/launch" className="card-how-it-works-item w-inline-block">
              <p className="text-caption-pill text-alignment-right">3 min</p>
              <div className="div-block-30" />
              <img
                width="20"
                height="20"
                alt=""
                loading="lazy"
                src="/assets/images/6758d7c1dd7aea7c5dbb2879_code.svg"
                className="card-how-it-works-icon"
              />
              <h4 className="text-heading-h5">3. Add your cron workflow steps</h4>
              <p className="text-body text-alignment-left">Define with crontab and Python or TypeScript</p>
              <div className="card-how-it-works-overlay" />
              <img
                src="/assets/images/6758d7795b9750161dca3338_annotations.png"
                loading="lazy"
                alt=""
                className="card-how-it-works-background"
              />
            </a>
            <a href="https://console.dbos.dev/launch" className="card-how-it-works-item w-inline-block">
              <p className="text-caption-pill text-alignment-right">1 min</p>
              <div className="div-block-30" />
              <img
                width="20"
                height="20"
                alt=""
                loading="lazy"
                src="/assets/images/6758d7c1fbb307648e5499cd_zap.svg"
                className="card-how-it-works-icon"
              />
              <h4 className="text-heading-h5">4. Deploy with a single click</h4>
              <p className="text-body text-alignment-left">And run reliably, forever, with full observability.</p>
              <div className="card-how-it-works-overlay" />
              <img
                src="/assets/images/6758d779fbb307648e545cbb_redeploy.png"
                loading="lazy"
                alt=""
                className="card-how-it-works-background"
              />
            </a>
          </div>
        </div>
      </section>
      <div className="spacer-3x" />
      <section className="section-wrapper">
        <div className="card-use-cases">
          <h2 className="text-heading-caption">Endless possibilities</h2>
          <h3 className="text-heading-h2">Cron Job Use Cases</h3>
          <div className="w-layout-hflex card-use-cases-row">
            <div className="w-layout-vflex card-use-cases-item">
              <h4 className="text-heading-h5">Record a new stock price every minute</h4>
              <p className="text-body text-alignment-left">
                Guarantee zero gaps in your data, with auto-recovery in all workflows.
              </p>
            </div>
            <div className="w-layout-vflex card-use-cases-item">
              <h4 className="text-heading-h5">Migrate across databases every hour</h4>
              <p className="text-body text-alignment-left">
                DBOS handles retries and recovery, preventing manual intervention.
              </p>
            </div>
            <div className="w-layout-vflex card-use-cases-item">
              <h4 className="text-heading-h5">Send a newsletter to your leads each month</h4>
              <p className="text-body text-alignment-left">
                Trace every instance and reach your audience without duplication.
              </p>
            </div>
            <div className="w-layout-vflex card-use-cases-item">
              <h4 className="text-heading-h5">Generate daily analytics reports for stakeholders</h4>
              <p className="text-body text-alignment-left">
                Aggregate multiple sources on time, and guarantee delivery.
              </p>
            </div>
          </div>
          <div className="card-insight-2-container">
            <a href="/blog/how-to-build-cloud-cron-jobs" target="_blank" className="card-insight-large w-inline-block">
              <img
                src="/assets/images/675a5852909d3e851652284f_build-a-better-cron-snippet.png"
                loading="lazy"
                alt=""
                sizes="100vw"
                srcSet="/assets/images/675a5852909d3e851652284f_build-a-better-cron-snippet-p-500.png 500w, /assets/images/675a5852909d3e851652284f_build-a-better-cron-snippet-p-800.png 800w, /assets/images/675a5852909d3e851652284f_build-a-better-cron-snippet.png 1238w"
                className="card-insight-image"
              />
              <div className="card-insight-large-content">
                <h4 className="text-heading-h4 text-heading-h4-insight">How to Build A Better Cron For The Cloud</h4>
                <p className="text-body text-alignment-left">
                  Lightweight durable execution library versus heavyweight durable workflow orchestration services,
                  making programs crashproof without changing the way they're built
                </p>
                <div className="card-insight-meta">
                  <div className="w-layout-hflex profile-small profile-small-container">
                    <img
                      src="/assets/images/675a594aad547588f6723c19_chuck-bear.png"
                      loading="lazy"
                      alt=""
                      className="profile-small-image"
                    />
                    <div className="text-body text-body-highlight">Chuck Bear</div>
                  </div>
                  <div className="text-caption-micro">July 1, 2024</div>
                </div>
              </div>
            </a>
            <a
              href="/webcast/stonebraker-increase-software-engineering-productivity"
              className="card-insight-large w-inline-block"
            >
              <img
                src="/assets/images/675a5852b2e31caf1ebb0c6b_increase-software-productivity-mike-stonebraker-webinar.png"
                loading="lazy"
                alt=""
                sizes="100vw"
                srcSet="/assets/images/675a5852b2e31caf1ebb0c6b_increase-software-productivity-mike-stonebraker-webinar-p-500.png 500w, /assets/images/675a5852b2e31caf1ebb0c6b_increase-software-productivity-mike-stonebraker-webinar-p-800.png 800w, /assets/images/675a5852b2e31caf1ebb0c6b_increase-software-productivity-mike-stonebraker-webinar-p-1080.png 1080w, /assets/images/675a5852b2e31caf1ebb0c6b_increase-software-productivity-mike-stonebraker-webinar.png 1238w"
                className="card-insight-image"
              />
              <div className="card-insight-large-content">
                <h4 className="text-heading-h4 text-heading-h4-insight">Increasing Software Productivity (10x)</h4>
                <p className="text-body text-alignment-left">
                  Lightweight durable execution library versus heavyweight durable workflow orchestration services,
                  making programs crashproof without changing the way they're built
                </p>
                <div className="card-insight-meta">
                  <div className="w-layout-hflex profile-small profile-small-container">
                    <img
                      src="/assets/images/675a594a3107ecdc86d8ebac_mike-stonebraker.png"
                      loading="lazy"
                      alt=""
                      className="profile-small-image"
                    />
                    <div className="text-body text-body-highlight">Mike Stonebraker</div>
                  </div>
                  <div className="text-caption-micro">June 20, 2024</div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>
      <div className="spacer-3x" />
      <section className="section-wrapper">
        <div className="card-accordion-faq-alt">
          <div className="w-layout-hflex accordion-content-left">
            <h2 className="text-heading-caption">FAQs</h2>
            <h3 className="text-heading-h2">Common Questions</h3>
            <p className="text-body text-alignment-left text-margin-bottom">
              We’ll do our best to cover all bases.
              <br />
              In case you need more detail or have additional questions, speak with our team.
            </p>
            <a href="/contact" className="button-cta button-secondary w-inline-block">
              <div className="text-block">Speak with our team</div>
            </a>
          </div>
          <div className="w-layout-hflex accordion-content-right">
            <Dropdown data-hover="false" data-delay="0" className="card-accordion-faq w-dropdown">
              <DropdownToggle className="dropdown-toggle w-dropdown-toggle">
                <h3 className="text-accordion-title">What are cron jobs?</h3>
                <div className="code-embed-2 w-embed">+</div>
              </DropdownToggle>
              <DropdownList className="dropdown-list w-dropdown-list">
                <div className="text-body-rich w-richtext">
                  <p>
                    Cron jobs are software scripts that run periodically, at specific times or intervals. They are used
                    to automate repetitive tasks such as file transfers, data collection, ETL jobs, sending
                    notifications, initiating workflows, and more.
                  </p>
                </div>
              </DropdownList>
            </Dropdown>
            <Dropdown data-hover="false" data-delay="0" className="card-accordion-faq w-dropdown">
              <DropdownToggle className="dropdown-toggle w-dropdown-toggle">
                <h3 className="text-accordion-title">What are my cron job hosting options?</h3>
                <div className="code-embed-2 w-embed">+</div>
              </DropdownToggle>
              <DropdownList className="dropdown-list w-dropdown-list">
                <div className="text-body-rich w-richtext">
                  <p>
                    {
                      'You can deploy durable cron jobs created with the open source DBOS\u00a0Transact library to any cloud and even on premises.'
                    }
                  </p>
                  <p>
                    {
                      'DBOS\u00a0also provides a serverless hosting platform - DBOS\u00a0Cloud - for durable schedule cron job execution. It provides automatic restart/resume if the job fails, it automates observability, and it includes a free AWS\u00a0RDS\u00a0instance for storing cron job state and application data.'
                    }
                  </p>
                </div>
              </DropdownList>
            </Dropdown>
            <Dropdown data-hover="false" data-delay="0" className="card-accordion-faq w-dropdown">
              <DropdownToggle className="dropdown-toggle w-dropdown-toggle">
                <h3 className="text-accordion-title">What happens if a Cron job fails?</h3>
                <div className="code-embed-2 w-embed">+</div>
              </DropdownToggle>
              <DropdownList className="dropdown-list w-dropdown-list">
                <div className="text-body-rich w-richtext">
                  <p>
                    If the server on which a cron job runs fails, or any of the steps executed by the cron job fail,
                    someone has to restart and rerun the cron job. This can lead to errors caused by steps that never
                    execute or execute multiple times.
                    <br />
                    <br />
                    {
                      'DBOS\u00a0eliminates these problems by automatically restarting and resuming failed jobs or workflow steps--exactly as they were meant to execute. It truly enables set-and-forget cron workflow processing.'
                    }
                  </p>
                </div>
              </DropdownList>
            </Dropdown>
          </div>
        </div>
      </section>
      <div className="spacer-3x" />
    </SiteLayout>
  );
}
